import { useApp, useTriggerAction } from '@libs/hooks';
import { TWidget } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import React from 'react';
import { TCardBalanceData, TCardBalanceDataMessages } from '../../../types/data';
import { ButtonPillView } from '../../Buttons/ButtonPillView';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { ButtonFlat } from '../../Buttons/ButtonFlat';
import { ZendeskChat } from '../../KYC/ZendeskChat';
import { CancelAndReissueCard } from './modals/CancelAndReissueCard';
import { CardDetails } from './modals/CardDetails';
import { InfoModal } from './modals/InfoModal';
import styles from './paysendCard.module.css';
import { CardBalanceActionName, getFormatExpirationDate } from './utils/utils';

type TCardBottomButtons = {
    widget: TWidget<TCardBalanceData>;
    suspiciousActivity: boolean;
    isExpired: boolean;
    isFrozen: boolean;
    messages: TCardBalanceDataMessages;
    closeScreen: () => void;
};
export const CardBottomButtons = ({ widget, isFrozen, isExpired, suspiciousActivity, messages, closeScreen }: TCardBottomButtons) => {
    const notExpAndNotSuspicious = !suspiciousActivity && !isExpired;
    const zendeskChat = widget.data?.zendeskChat;
    const { setPopup, setModal } = useApp();
    const { triggerActionsByType } = useTriggerAction(widget);

    return (
        <>
            <div className={styles.cardBalanceButtons}>
                <ConditionalRender condition={suspiciousActivity && !!zendeskChat}>
                    {zendeskChat && (
                        <ZendeskChat
                            isWidget={false}
                            zendeskToken={zendeskChat.zendeskToken}
                            extras={zendeskChat.extras}
                            label={widget.data?.messages?.contactSupportBtn}
                        />
                    )}
                </ConditionalRender>
                <ConditionalRender condition={!suspiciousActivity && isExpired}>
                    <ButtonRowView
                        label={messages.reissueCardBtn}
                        iconName={'ic_arrows_exchange'}
                        onClick={() => setPopup(<CancelAndReissueCard widget={widget} messages={messages} />)}
                    />
                </ConditionalRender>
                <ConditionalRender condition={notExpAndNotSuspicious && !isFrozen}>
                    <ButtonFlat
                        label={messages.cardDetailsBtn}
                        iconName={'ic_eye'}
                        onClick={() => {
                            triggerActionsByType(CardBalanceActionName.cardDetails, { isShowLoader: true }).then((newWidget) => {
                                const newData = newWidget?.data;
                                if (newData?.pan) {
                                    setPopup(
                                        <CardDetails
                                            pan={newData?.pan}
                                            cardholderName={newData?.cardholderName}
                                            expirationDate={getFormatExpirationDate(newData?.expirationDate)}
                                            cvv={newData?.cvv}
                                            messages={messages}
                                        />,
                                    );
                                } else if (newWidget) {
                                    setModal(
                                        <InfoModal
                                            text={newWidget.data?.messages?.errorMessage ?? widget.data?.messages?.errorMessage ?? ''}
                                            success={false}
                                        />,
                                    );
                                }
                            });
                        }}
                    />
                    <ButtonFlat
                        label={messages.freezeCardBtn}
                        iconName={'ic_snowflake'}
                        onClick={() => {
                            triggerActionsByType(CardBalanceActionName.freezeCard, { isShowLoader: true }).catch((e) => console.log(e));
                        }}
                    />
                    <ButtonFlat
                        label={messages.moreBtn}
                        iconName={'ic_dots'}
                        onClick={() => setPopup(<CancelAndReissueCard widget={widget} messages={messages} />)}
                    />
                </ConditionalRender>
                <ConditionalRender condition={notExpAndNotSuspicious && isFrozen}>
                    <ButtonFlat
                        label={messages.unfreezeCardBtn}
                        iconName={'ic_snowflake_crossed'}
                        onClick={() => {
                            triggerActionsByType(CardBalanceActionName.unfreezeCard, { isShowLoader: true }).catch((e) => console.log(e));
                        }}
                    />
                    <ButtonFlat
                        label={messages.reissueCardBtn}
                        iconName={'ic_arrow_retry'}
                        onClick={() => setPopup(<CancelAndReissueCard widget={widget} messages={messages} />)}
                    />
                    {zendeskChat && (
                        <ZendeskChat
                            isFloatButton
                            isWidget={false}
                            zendeskToken={zendeskChat.zendeskToken}
                            extras={zendeskChat.extras}
                            label={widget.data?.messages?.contactSupportBtn}
                        />
                    )}
                </ConditionalRender>
            </div>
            <div className={styles.cardBalanceDone}>
                <ButtonPillView maxWidth="100%" label={messages.done} position="floating" hierarchy="primary" onClick={() => closeScreen()} />
            </div>
        </>
    );
};
